import { Lock, Rocket, Undo2 } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { DeploymentKind, EnvironmentView, LandingProjectConfig, LandingState } from '@orgus/protocol';
import { Button, Dialog, Input, Select, toUiError, toast } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { requestDeploy } from './api.ts';
import { deployableVersions } from './model.ts';

export interface DeployIntent {
  kind: DeploymentKind;
  env: EnvironmentView;
  /** promote: where the version comes from. */
  from?: EnvironmentView;
  /** rollback: the version to go back to. */
  to?: string;
}

const VERB: Record<DeploymentKind, string> = { deploy: 'Deploy', promote: 'Promote', rollback: 'Roll back' };

/**
 * Deploy, promote or roll back one environment. Names exactly what changes; a protected
 * environment (production) needs the version typed to confirm.
 */
export function DeployDialog({ intent, onClose, state, config, projectName }: { intent: DeployIntent | null; onClose: () => void; state: LandingState; config: LandingProjectConfig; projectName: string }) {
  const [version, setVersion] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const versions = useMemo(() => deployableVersions(state, config.projectId), [state, config.projectId]);
  const env = intent?.env;

  useEffect(() => {
    if (!intent) return;
    setVersion(intent.kind === 'promote' ? (intent.from?.current?.version ?? '') : intent.kind === 'rollback' ? (intent.to ?? '') : (versions.find((v) => v.version !== intent.env.current?.version)?.version ?? ''));
    setConfirm('');
    setError(null);
    // Reset when a new intent opens.
  }, [intent]);

  if (!intent || !env) return null;
  const name = env.config.name;
  const current = env.current?.version;
  const title = intent.kind === 'promote' ? `Promote ${version} from ${intent.from?.config.name} to ${name}?` : intent.kind === 'rollback' ? `Roll ${name} back to ${version}?` : `Deploy to ${name}`;
  const picked = versions.find((v) => v.version === version);
  const needsConfirm = env.config.protected;
  const ready = !!version && version !== current && (!needsConfirm || confirm.trim().toLowerCase() === version.toLowerCase());

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = intent.kind === 'promote' ? { from: intent.from?.config.name, confirm } : intent.kind === 'rollback' ? { to: version, confirm } : { version, confirm };
      const d = await requestDeploy(intent.kind, config.projectId, name, body, config);
      toast.success(`${VERB[intent.kind]} started`, `${d.version} to ${name} in ${projectName}. It goes live when the deploy command finishes.`);
      onClose();
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && !busy && onClose()}
      dismissible={!busy}
      size="md"
      title={title}
      description={
        <>
          {current ? (
            <>
              <span className="font-mono">{current}</span> is live in {name} now and is replaced.
            </>
          ) : (
            `Nothing is live in ${name} yet.`
          )}{' '}
          The deploy command runs on a fresh checkout of the exact commit, on an eligible machine.
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant={needsConfirm ? 'danger' : 'primary'} icon={intent.kind === 'rollback' ? <Undo2 /> : <Rocket />} loading={busy} disabled={!ready} onClick={run}>
            {VERB[intent.kind]} {version || ''}
          </Button>
        </>
      }
    >
      <form
        onKeyDown={(e) => {
          // Enter in a text field sends the form, as a submit button would.
          if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
            e.preventDefault();
            e.currentTarget.requestSubmit();
          }
        }}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) void run();
        }}
      >
        {intent.kind === 'deploy' ? (
          <Select
            label="Version"
            value={version}
            placeholder={versions.length ? 'Pick a version' : 'Nothing has landed yet'}
            options={versions.map((v) => ({ value: v.version, label: `${v.version}  ${v.title}`, disabled: v.version === current }))}
            onValueChange={setVersion}
            hint="Only versions that landed through the queue can be deployed."
          />
        ) : (
          <div className="rounded-card border border-border-subtle bg-surface px-3 py-2 text-ui">
            <p className="flex items-center gap-2">
              <span className="font-mono text-fg">{version}</span>
              <span className="truncate text-fg-muted">{picked?.title ?? ''}</span>
            </p>
            {intent.kind === 'rollback' && env.frozen && <p className="mt-1 text-ui text-status-attention">{name} is frozen ({env.frozen.reason}). A rollback is still allowed and is audited.</p>}
          </div>
        )}
        {needsConfirm && (
          <Input
            label={
              <span className="flex items-center gap-2">
                <Lock className="size-4" aria-hidden />
                {name} is protected. Type the version to confirm.
              </span>
            }
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={version}
            spellCheck={false}
            autoComplete="off"
            className="font-mono"
            hint="Only an admin or the owner can change a protected environment."
          />
        )}
        {error && (
          <p role="alert" className="text-ui text-status-danger">
            {error.what} {error.why} {error.next}
          </p>
        )}
      </form>
    </Dialog>
  );
}
